/**
 * Usage and its alerts (S12).
 *
 * ## Why this is our arithmetic and not the platform's
 *
 * Sirma gives no client-visible signal when it terminates a call for a limit
 * (Q4, R8): the webhook still says the session completed, the platform log still
 * says SUCCESS, and nothing carries a balance or a cap. So an alert about spend
 * can only come from counting what `/usage` reports against a budget we hold
 * ourselves — which is also why the warning threshold sits at 70% rather than
 * nearer the edge. There is no second warning behind it.
 *
 * ## Why usage has to be attributed to one API key
 *
 * `/usage` reports per project, and each project's figures are broken down by
 * API key. The dev project holds five keys — other demos, an n8n integration —
 * and their combined audio is roughly four times this console's. Storing the
 * project total would raise alerts about somebody else's traffic. In production
 * the client gets their own project, where the total *is* theirs; so which of the
 * two to read is configuration, not a guess (C7).
 */
import { z } from 'zod';

import { NotificationSeverity } from './enums.js';

/** A month's usage against the budget, as the dashboard and alerts see it. */
export const usageSummarySchema = z.object({
  /** The calendar month being reported, e.g. `2026-08`. */
  period: z.string(),
  periodStart: z.string().datetime(),
  periodEnd: z.string().datetime(),
  /** Voice minutes consumed, rounded to a minute — the unit the budget is in. */
  voiceMinutesUsed: z.number().min(0),
  voiceMinutesBudget: z.number().int().min(0),
  /** Null when the budget is zero, which means "no budget set" rather than "all used". */
  pctUsed: z.number().min(0).nullable(),
  /** Where this sits against the thresholds. */
  severity: NotificationSeverity.schema,
  /**
   * The date the budget runs out at the current daily pace, or null.
   *
   * Null when nothing has been used, when the pace would not exhaust it this
   * month, or when there is too little of the month elapsed to extrapolate from
   * — a projection off two days of a thirty-day month is a guess wearing a date.
   */
  projectedExhaustion: z.string().nullable(),
  /** Which key these figures cover, or null when the whole project is counted. */
  attributedTo: z.string().nullable(),
  /** When the figures were last read from the platform. */
  fetchedAt: z.string().datetime().nullable(),
});
export type UsageSummary = z.infer<typeof usageSummarySchema>;

export const usageThresholdsSchema = z.object({
  voiceMinutesMonthlyBudget: z.coerce.number().int().min(0),
  warnPct: z.coerce.number().int().min(1).max(100),
  critPct: z.coerce.number().int().min(1).max(100),
});
export type UsageThresholds = z.infer<typeof usageThresholdsSchema>;

/**
 * Which band a percentage falls in.
 *
 * Shared so the alert and the dashboard card can never disagree about whether
 * 70.4% is a warning — the sort of split that has one screen amber and another
 * green at the same instant.
 */
export function severityFor(
  pctUsed: number | null,
  thresholds: UsageThresholds,
): 'INFO' | 'WARNING' | 'CRITICAL' {
  if (pctUsed === null) return 'INFO';
  if (pctUsed >= thresholds.critPct) return 'CRITICAL';
  if (pctUsed >= thresholds.warnPct) return 'WARNING';
  return 'INFO';
}
